import React, { useEffect, useRef, useState } from 'react';
import { Card, Container, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { toast } from 'react-toastify';

import BenchmarkHeader from './components/BenchmarkHeader';
import BenchmarkFilters from './components/BenchmarkFilters';
import BenchmarkKpis from './components/BenchmarkKpis';
import EmissionTrendChart from './components/EmissionTrendChart';
import BenchmarkAIInsights from './components/BenchmarkAIInsights';

import {
  getSmartBenchmarkingOverviewApi,
  getEmissionTrendApi,
  getSmartBenchmarkingInsightsApi,
  getSmartBenchmarkingInsightsWorkerApi,
} from './smartBenchmarkApi';

import {
  getCurrentReportPeriod,
  getReportRangePeriod
} from '../reports/reportHelper';

import { parseUserInfo } from '../../utilities/common';

const normalizeInsights = (data) => {
  if (!data || !Array.isArray(data.sections)) {
    return null;
  }

  return {
    title: data.title || 'AI-Powered Insights',
    sections: data.sections.filter((section) => (
      ['trend', 'anomaly', 'recommendation'].includes(section?.type)
    )),
    footerNote: data.footerNote || '',
  };
};

const SmartBenchmarkPage = () => {

  const { t } = useTranslation();
  const reportContentRef = useRef(null);

  const { orgId, usrTimezone } =
    parseUserInfo(useSelector((state) => state.users.user));

  const userInfo = useSelector((state) => state.users.user);
  const BUSINESS_TYPE_REVENUE_ID = userInfo.usrOrgOptions?.optBusinessMeasureIds?.['REVENUE-BMK'];

  const currentReportPeriod = getCurrentReportPeriod(userInfo.usrOrgOptions);
  const optOrgSectorEnabled = userInfo?.usrOrgOptions?.optOrgSectorEnabled;

  const reportRangePeriod = getReportRangePeriod(
    userInfo.usrOrgOptions,
    currentReportPeriod.reportYear,
    true,
  );

  const [startDate, setStartDate] = useState(reportRangePeriod.startDate);
  const [endDate, setEndDate] = useState(reportRangePeriod.endDate);

  const [overview, setOverview] = useState(null);
  const [trend, setTrend] = useState(null);
  const [insights, setInsights] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isInsightsLoading, setIsInsightsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const insightsPollRef = useRef(null);

  useEffect(() => {

    if (!orgId || !optOrgSectorEnabled) return;

    const payload = {
      startDate,
      endDate,
      usrTimezone,
      intervalType: 'monthly',
      businessTypeId: Number(BUSINESS_TYPE_REVENUE_ID),
      entitiesIdsToInclude: [],
      epsIdsToInclude: []
    };

    setIsLoading(true);
    setIsInsightsLoading(true);
    setInsights(null);
    clearTimeout(insightsPollRef.current);

    let cancelled = false;

    Promise.all([
      getSmartBenchmarkingOverviewApi(orgId, payload),
      getEmissionTrendApi(orgId, payload),
    ])
      .then(([overviewRes, trendRes]) => {
        if (cancelled) return;
        setOverview(overviewRes);
        setTrend(trendRes);
      })
      .catch(() => {
        if (cancelled) return;
        setOverview(null);
        setTrend(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    getSmartBenchmarkingInsightsApi(orgId, payload)
      .then((worker) => {
        if (cancelled) return;
        let pollAttempts = 0;
        const MAX_POLL_ATTEMPTS = 20;
        const pollWorker = async () => {
          try {
            const result = await getSmartBenchmarkingInsightsWorkerApi(orgId, worker.id);
            if (cancelled) return;
            if (result.status === 'done') {
              setInsights(normalizeInsights(result.result));
              setIsInsightsLoading(false);
            } else if (result.status === 'error') {
              setInsights(null);
              setIsInsightsLoading(false);
              toast.error(t('An error occurred while generating AI insights. Please try again later.'));
            } else {
              pollAttempts += 1;
              if (pollAttempts >= MAX_POLL_ATTEMPTS) {
                setInsights(null);
                setIsInsightsLoading(false);
                toast.error(t('AI insights are taking too long to generate. Please try again later.'));
                return;
              }
              insightsPollRef.current = setTimeout(pollWorker, 10000);
            }
          } catch (err) {
            if (cancelled) return;
            setInsights(null);
            setIsInsightsLoading(false);
            toast.error(t('An error occurred while generating AI insights. Please try again later.'));
          }
        };
        pollWorker();
      })
      .catch(() => {
        if (cancelled) return;
        setInsights(null);
        setIsInsightsLoading(false);
        toast.error(t('An error occurred while generating AI insights. Please try again later.'));
      });

    return () => {
      cancelled = true;
      clearTimeout(insightsPollRef.current);
    };

  }, [orgId, startDate, endDate, usrTimezone, optOrgSectorEnabled, BUSINESS_TYPE_REVENUE_ID, t]);

  const hasOverviewData = overview && [
    overview.emissionIntensity?.value,
    overview.sectorBenchmark?.value,
    overview.variance?.value,
  ].some((value) => value !== null && value !== undefined);
  const canExportReport = !isLoading && !isInsightsLoading && hasOverviewData && trend && insights;
  const reportingPeriod = `${format(new Date(startDate), 'MMM yyyy')} - ${format(new Date(endDate), 'MMM yyyy')}`;

  const handleExportReport = async () => {
    if (!reportContentRef.current || !canExportReport || isExporting) {
      return;
    }

    try {
      setIsExporting(true);

      const canvas = await html2canvas(reportContentRef.current, {
        scale: 1.5,
        useCORS: true,
        backgroundColor: '#ffffff',
        onclone: (clonedDocument) => {
          const clonedReport = clonedDocument.getElementById('smart-benchmark-report-export');
          const clonedHeader = clonedDocument.getElementById('benchmark-header-export');
          clonedHeader.style.display = 'block';
          if (clonedReport) {
            clonedReport.style.maxWidth = '1400px';
            clonedReport.style.width = '1400px';
            clonedReport.style.margin = '0 auto';
          }
        },
      });

      const pdf = new jsPDF({ orientation: 'p', unit: 'pt', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 24;
      const contentWidth = pageWidth - margin * 2;
      const availablePageHeight = pageHeight - margin * 2;
      const pageCanvasHeight = Math.floor((availablePageHeight * canvas.width) / contentWidth);
      const totalPages = Math.ceil(canvas.height / pageCanvasHeight);

      for (let pageIndex = 0; pageIndex < totalPages; pageIndex += 1) {
        if (pageIndex > 0) {
          pdf.addPage();
        }

        const sliceCanvas = document.createElement('canvas');
        const sliceContext = sliceCanvas.getContext('2d');
        const sourceY = pageIndex * pageCanvasHeight;
        const currentSliceHeight = Math.min(pageCanvasHeight, canvas.height - sourceY);

        sliceCanvas.width = canvas.width;
        sliceCanvas.height = currentSliceHeight;

        sliceContext.drawImage(
          canvas,
          0,
          sourceY,
          canvas.width,
          currentSliceHeight,
          0,
          0,
          canvas.width,
          currentSliceHeight
        );

        const pageImageData = sliceCanvas.toDataURL('image/jpeg', 0.9);
        const renderedHeight = (currentSliceHeight * contentWidth) / canvas.width;

        pdf.addImage(
          pageImageData,
          'JPEG',
          margin,
          margin,
          contentWidth,
          renderedHeight
        );
      }

      pdf.save(`smart-benchmarking-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    } catch (error) {
      toast.error(t('Unable to export report'));
    } finally {
      setIsExporting(false);
    }
  };

  if (!optOrgSectorEnabled) {
    return (
      <Container fluid className="pt-4 px-4 pb-4">
        <Card className="mb-4">
          <Card.Body className="text-center text-muted py-4">
            {t('Your organization has not enabled sector benchmarking yet. Please contact your administrator to activate it.')}
          </Card.Body>
        </Card>
      </Container>
    )
  }

  if (!BUSINESS_TYPE_REVENUE_ID) {
    return (
      <Container fluid className="pt-4 px-4 pb-4">
        <Card className="mb-4">
          <Card.Body className="text-center text-muted py-4">
            {t('"Revenue (£M)" business measure is required to generate the smart benchmarking report. Please contact your administrator to set it up.')}
          </Card.Body>
        </Card>
      </Container>
    )
  }

  return (
    <Container fluid className="pt-4 px-4 pb-4">

      <BenchmarkHeader
        canExport={canExportReport}
        isExporting={isExporting}
        onExport={handleExportReport}
      />

      <BenchmarkFilters
        isLoading={isLoading}
        startDate={startDate}
        endDate={endDate}
        onApply={({ startDate, endDate }) => {
          setStartDate(startDate);
          setEndDate(endDate);
        }}
      />

      {isLoading && (
        <Container fluid className="py-5 text-center">
          <Spinner animation="border" />
        </Container>
      )}

      {!isLoading && !hasOverviewData && (
        <Card className="mb-4">
          <Card.Body className="text-center text-muted py-4">
            {t('No data available')}
          </Card.Body>
        </Card>
      )}

      {!isLoading && hasOverviewData && (
        <div
          id="smart-benchmark-report-export"
          ref={reportContentRef}
        >
          <div id="benchmark-header-export" style={{display: 'none'}}>
            <BenchmarkHeader
              reportingPeriod={reportingPeriod}
              showExportButton={false}
            />
          </div>

          <BenchmarkKpis overview={overview} />

          {trend && (
            <EmissionTrendChart trend={trend} />
          )}

          <BenchmarkAIInsights
            insights={insights}
            isLoading={isInsightsLoading}
            benchmarkSource={overview?.sectorBenchmark?.source}
          />
        </div>
      )}

    </Container>
  );
};

export default SmartBenchmarkPage;
